import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getDefaultStore } from 'jotai';
import {
  gatewayDownReportsAtom,
  clearGatewayDownReports,
  type DownBackend,
} from '@/modules/backend-down-signal';
import { pingSanrApi } from '@/modules/sanr-api';
import { pingArenaApi } from '@/modules/arena-api';
import { track } from '@/shared/utils/track';
import { zOverModalBanner } from '@/shared/constants/zIdxs';
import { cn } from '@/modules/cn';

const POLL_MS = 2000;
const RECOVERED_BANNER_MS = 2000;

const PROBES: Record<DownBackend, () => Promise<boolean>> = {
  sanr: pingSanrApi,
  arena: pingArenaApi,
};

// Gateway-outage handling (backend redeploy windows): flipping react-query's
// onlineManager offline pauses every query on its cached data instead of
// erroring, and paused queries resume on their own once it is back — the
// invalidateQueries() sweep on recovery is for whatever errored before the
// pause. Mutations are deliberately NOT paused (networkMode 'always' on the
// QueryClient): a write clicked during the window must fail fast and
// honestly, never queue silently and auto-fire on recovery.
export default function BackendStatusProvider({
  children,
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<'up' | 'down' | 'recovered'>('up');

  useEffect(() => {
    let disposed = false;
    const down = new Set<DownBackend>();
    let downSince = 0;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let recoveredTimer: ReturnType<typeof setTimeout> | undefined;

    const schedulePoll = () => {
      pollTimer = setTimeout(() => {
        const backends = [...down];
        void Promise.all(backends.map((backend) => PROBES[backend]())).then(
          (results) => {
            if (disposed) return;
            backends.forEach((backend, i) => {
              if (results[i] === true) down.delete(backend);
            });
            if (down.size > 0) {
              schedulePoll();
              return;
            }
            onlineManager.setOnline(true);
            void queryClient.invalidateQueries();
            track('backend_unavailable_recovered', {
              duration_seconds: Math.round((Date.now() - downSince) / 1000),
            });
            setPhase('recovered');
            recoveredTimer = setTimeout(
              () => setPhase('up'),
              RECOVERED_BANNER_MS,
            );
          },
        );
      }, POLL_MS);
    };

    // Reports can arrive before this mount (the atom buffers them), hence
    // the immediate drain; clearing after each drain keeps the atom a
    // buffer, not a history.
    const store = getDefaultStore();
    const processReports = () => {
      const reports = store.get(gatewayDownReportsAtom);
      if (reports.size === 0) return;
      clearGatewayDownReports();
      for (const backend of reports) {
        const wasIdle = down.size === 0;
        down.add(backend);
        if (!wasIdle) continue;
        clearTimeout(recoveredTimer);
        onlineManager.setOnline(false);
        downSince = Date.now();
        track('backend_unavailable_shown');
        setPhase('down');
        schedulePoll();
      }
    };
    const unsubscribe = store.sub(gatewayDownReportsAtom, processReports);
    processReports();

    return () => {
      disposed = true;
      unsubscribe();
      clearTimeout(pollTimer);
      clearTimeout(recoveredTimer);
      // Never leave the shared onlineManager stuck offline past this
      // provider's life — nothing else would ever flip it back.
      if (down.size > 0) onlineManager.setOnline(true);
    };
  }, [queryClient]);

  return (
    <>
      {children}
      {phase !== 'up' &&
        createPortal(
          <Banner recovered={phase === 'recovered'} />,
          document.body,
        )}
    </>
  );
}

function Banner({ recovered }: { recovered: boolean }) {
  return (
    <div
      role="status"
      style={{ zIndex: zOverModalBanner }}
      className={cn(
        'fixed inset-x-0 top-0 flex items-center justify-center gap-2 border-b px-4 py-2 text-body-sm font-medium',
        recovered ?
          'border-green-03 bg-green-01 text-green-default'
        : 'border-orange-03 bg-orange-01 text-orange-default',
      )}
    >
      {recovered ?
        'Back online'
      : <>
          <Loader2 size={16} className="animate-spin" />
          We’re updating Score Arena — back in a moment
        </>
      }
    </div>
  );
}
